import { memo } from "react";
import {
  buildPath,
  MimicField,
  MimicForm,
  MimicFormik,
  MimicFormSaveButton,
  TextField,
  useGlobalLoaderRemover,
  useRefetchQuery,
} from "@tecorien/mimic";
import { SignatureCardBlock } from "components/Signature/SignatureCardBlock";
import { prettifySignature } from "components/Signature/utils";
import { GetUserSettingsDocument, UserDtoInput, useUpdateMeMutation } from "generated/graphql";
import { useKeycloak } from "keycloak";
import { SignatureSettings } from "types";
import { useSimpleOnSubmit } from "utils/form";
import { validationSchema } from "./validationSchema";

import styles from "./styles.module.scss";

type MeDtoInput = Omit<UserDtoInput, "signature"> & { signature?: SignatureSettings | null };

export type UserProfileFormProps = {
  me: MeDtoInput
}

export const UserProfileForm = memo<UserProfileFormProps>(({ me }) => {
  useGlobalLoaderRemover();
  const [ update ] = useUpdateMeMutation();
  const refetchQuery = useRefetchQuery(GetUserSettingsDocument);
  const { keycloak } = useKeycloak();

  return (
    <MimicFormik
      onSubmit={useSimpleOnSubmit({
        submit: async (values, formikHelpers, setResultsToForm) => {
          const { errors, data } = await update({ variables: { values } });
          if (data) {
            setResultsToForm(data.me?.success, data.me?.data, errors);
          } else {
            setResultsToForm(false, null, errors);
          }
        },
        onSuccess: async () => {
          await refetchQuery();
          await keycloak.refreshUserToken(Number.MAX_VALUE);
        },
      })}
      initialValues={me}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values }) =>
        <MimicForm renderHiddenSubmitButton={false} className={styles.root}>
          <div className={styles.content}>
            <h1 className={styles.title}>Please, fill your information</h1>

            <MimicField
              name={buildPath<MeDtoInput>()("firstName")}
              label="First name"
              component={TextField}
              className={styles.field}
            />

            <MimicField
              name={buildPath<MeDtoInput>()("lastName")}
              label="Last name"
              component={TextField}
              className={styles.field}
            />
            <MimicField
              name={buildPath<MeDtoInput>()("signature")}
              component={SignatureCardBlock}
              optionText={prettifySignature(`${values.firstName || ""} ${values.lastName || ""}`)}
              formatMimic={(value: SignatureSettings | null) => value?.font}
              normalizeMimic={value => value ? ({ font: value } as SignatureSettings) : null}
              checkBoxClassName={styles.checkbox}
              className={styles.signatures}
            />
            <MimicFormSaveButton className={styles.submit}>
              Save
            </MimicFormSaveButton>
          </div>
        </MimicForm>
      }
    </MimicFormik>
  );
});

export default UserProfileForm;